/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import {
  all,
  call,
  put,
  getContext,
  select,
  delay,
  take,
} from "typed-redux-saga";
import { getGlobalConfigs } from "@/index";
import constants from "@/constants";
import { rootActions, RootState } from "@/store/";
import { last24Hours, last28Days } from "@/modules/dateFormats";
import createAlertsExpensive from "@/presentation/Alert/create.worker";
import { chunk } from "lodash/fp";
import {
  selectMachines,
  selectAreMachinesFiltered,
} from "@/store/filter/selectors";
import {
  selectIsMicroView,
  selectLastFetchedCarouselData,
  selectLastFetchedCycleTime,
} from "@/store/currentStatus/selectors";
import {
  createJobPresentation,
  mapStringToVerdict,
  mapStringToStatus,
  JobPresentation,
} from "@/presentation/Job";
import {
  Machine,
  MachineType,
  Alert,
  MasteringEvent,
  TimeSeriesValueCompactValue,
  JobSummary,
} from "@centralwebteam/narwhal";
import { CMSClientType } from "@/cms-api";
import uniqBy from "lodash/fp/uniqBy";
import orderBy from "lodash/fp/orderBy";
import { toUTCISOString } from "@/modules/dateFormats/index";
import { selectLastFetchedForRefresh } from "./selectors";
// Maximum number of GUID query strings per request, in order not to exceed the IIS default maxQueryString of 2048 characters
const GUID_QUERY_CHUNK_AMOUNT_MACHINES = 40;
const GUID_QUERY_CHUNK_AMOUNT_TIMESERIES = 35;
const GUID_QUERY_CHUNK_AMOUNT_TIMESERIES_MACHINES = 35;

export function* currentStatusMountedBG() {
  const now = toUTCISOString(new Date());
  try {
    yield* put(
      rootActions.currentStatus.setFetchingState({ fetchingStatus: "Fetching" })
    );
    yield* all([
      fetchAllMachineAlertsSaga(),
      fetchAllMachineJobsSaga(),
      fetchAllMachineStatusUpdates(),
      fetchCarouselData(),
    ]);
  } catch (error) {
    console.log(error);
  } finally {
    yield* put(
      rootActions.currentStatus.setFetchingState({
        fetchingStatus: "Idle",
        to: now,
      })
    );
  }
}

export function* liveRefreshWatcher() {
  const config = yield* call(() => getGlobalConfigs());
  while (true) {
    yield* delay(1000);
    const timeSinceLastFetchCompleted = yield* select(
      selectLastFetchedForRefresh
    );
    if (timeSinceLastFetchCompleted) {
      const now = new Date();
      const timeSinceLastFetch =
        now.getTime() - timeSinceLastFetchCompleted.getTime();
      if (timeSinceLastFetch < config.refreshRateFastInMs) {
        continue;
      }
      yield* put(
        rootActions.currentStatus.setFetchingState({
          fetchingStatus: "Fetching",
        })
      );
      yield* all([
        fetchAllMachineAlertsSaga(),
        fetchAllMachineJobsSaga(),
        fetchAllMachineStatusUpdates(),
        fetchCarouselData(),
      ]);
      yield* put(
        rootActions.currentStatus.setFetchingState({
          fetchingStatus: "Idle",
          to: toUTCISOString(now),
        })
      );
      yield* put(
        rootActions.currentStatus.liveRefreshCompleted({
          date: toUTCISOString(now),
        })
      );
    }
  }
}

/** Fetches extra data immediately when the viewMode switches to large */
export function* watchViewMode() {
  while (true) {
    const mode = yield* take(rootActions.currentStatus.setViewMode);
    if (mode.payload === "NORMAL") {
      yield* all([fetchCarouselData(), fetchMachineJobCycleTimeSaga()]);
    }
  }
}

function* fetchAllMachineJobsSaga() {
  try {
    const machines = yield* select(selectMachines);
    const chunkedMachines = chunk(GUID_QUERY_CHUNK_AMOUNT_MACHINES, machines);
    yield* all(
      chunkedMachines.map((machines) => call(fetchMachineJobs, machines))
    );
  } catch (error) {
    console.error(error);
  }
}

function* fetchMachineJobs(machines: Machine[]) {
  try {
    const client: CMSClientType = yield getContext("client");
    const { from, to } = yield* call(last24Hours);
    const limit = 10;
    const allMachineIds = machines.map((machine) => machine.id);
    let jobs = {} as Mappy<JobSummary[]>;
    try {
      jobs = yield* call(
        () =>
          client.jobs.tempRawSummary({
            machines: allMachineIds,
            from,
            to,
            take: limit,
          }).promise
      );
    } catch (ex) {
      console.warn(ex);
    }

    for (const machineId of allMachineIds) {
      let data =
        jobs[machineId] && typeof jobs[machineId] === "object"
          ? jobs[machineId]!.map((job) => createJobPresentation(job))
          : [];
      const type = machines.find((m) => m.id === machineId)?.type;
      if (type === MachineType.Equator) {
        let masters: MasteringEvent[] = yield* call(
          () =>
            // Fetch twice as many master events, because starts and ends are separate
            client.events.query<any[]>(
              `$filter=(machineId eq ${machineId})and(type eq 'Mastering')and(created ge ${from})and(created lt ${to})&$top=${
                limit * 2
              }&$orderBy=created desc`
            ).promise
        );
        if (!masters) masters = [];
        const masterStarts = masters
          .filter((job) => job.active)
          .sort((a, b) => a.created.localeCompare(b.created));
        const masterEnds = masters
          .filter((job) => !job.active)
          .sort((a, b) => a.created.localeCompare(b.created));
        const pairedMasters: JobPresentation[] = masterStarts.map(
          (start, index) => {
            const nextStart = masterStarts[index + 1];
            let end: MasteringEvent | undefined;
            // We don't always have a sequenceExecutionId, but use it if we do.
            if (start.sequenceExecutionId) {
              end = masterEnds.find(
                (j) => j.sequenceExecutionId === start.sequenceExecutionId
              );
            } else {
              end = masterEnds.find(
                (m) =>
                  m.created > start.created &&
                  (!nextStart?.created || m.created < nextStart.created)
              );
            }

            if (!end) {
              return {
                type: "mastering",
                id: start.id,
                name: start.name,
                machineId: start.machineId,
                start: start.created,
                end: nextStart?.created,
                status: "No Master End",
                verdict: "No Verdict",
              };
            }
            return {
              type: "mastering",
              id: start.id,
              name: start.name,
              machineId: start.machineId,
              start: start.created,
              end: end.created || nextStart?.created || undefined,
              passes: end.characteristicsPassed ?? 0,
              failures: end.characteristicsFailed ?? 0,
              warnings: end.characteristicsWarning ?? 0,
              notToleranced: end.characteristicsNotToleranced ?? 0,
              status: mapStringToStatus("mastering", end.status),
              verdict: mapStringToVerdict(end.verdict),
            };
          }
        );
        let allJobs = uniqBy("start", [...pairedMasters, ...data]);
        allJobs = orderBy(["start"], ["desc"], [...allJobs]);
        data = allJobs.length <= 10 ? allJobs : allJobs.slice(0, 10);
      }
      if (data && data.length > 0)
        yield* put(
          rootActions.currentStatus.fetchJobSummariesForMachine.success({
            id: machineId,
            data,
          })
        );
    }
  } catch (error) {
    console.error(error);
  }
}

export function* fetchMachineJobCycleTimeSaga() {
  // Don't load expensive cycle time data if we aren't showing it
  const micro = yield* select(selectIsMicroView);
  if (micro) return;

  // Call this less often than the other page data because it's slow
  const lastFetched = yield* select(selectLastFetchedCycleTime);
  const config = yield* call(() => getGlobalConfigs());
  if (
    lastFetched !== null &&
    Date.now() - Date.parse(lastFetched) < 4 * config.refreshRateFastInMs
  )
    return;

  try {
    const client: CMSClientType = yield getContext("client");
    const machines = yield* select(selectMachines);
    const { from, to } = yield* call(last28Days);
    const jobs = yield* select(
      (state: RootState) => state.currentStatus.machineJobSummaries
    );

    yield* all(
      machines.map(function* (machine) {
        const machineJobs = jobs[machine.id];
        if (!machineJobs) return;
        const latestJob = machineJobs[0];
        if (!latestJob) return;
        const cycleTime = yield* call(
          () =>
            client.machines.cycleTime({
              from,
              to,
              machineIds: [machine.id],
              jobName: latestJob.name,
            }).promise
        );
        if (cycleTime[0]) {
          yield* put(
            rootActions.currentStatus.fetchedMachineCycleData({
              machineId: machine.id,
              cycleTime: cycleTime[0],
            })
          );
        }
      })
    );
  } catch (error) {
    console.error(error);
  }
}

function* fetchAllMachineAlertsSaga() {
  try {
    const { from, to } = yield* call(last24Hours);
    const config = yield* call(() => getGlobalConfigs());
    yield* call(() => fetchAlertsSaga(from, to, config.maxTake));
  } catch (error) {
    console.error(error);
  }
}

function* fetchAlertsSaga(start: string, end: string, pageSize: number) {
  try {
    const client: CMSClientType = yield getContext("client");
    let skip = 0;
    let allAlerts: Alert[] = [];

    while (true) {
      const query = `$filter=(type eq 'Alert')and(created gt ${start})and(created lt ${end})&$top=${pageSize}&$skip=${skip}&$orderBy=created desc`;

      const alerts: Alert[] = yield* call(
        () => client.events.query<Alert[]>(query).promise
      );

      if (alerts?.length > 0) {
        allAlerts = allAlerts.concat(alerts);
        skip += alerts.length;

        // If fewer than pageSize returned, we're done
        if (alerts.length < pageSize) break;
      } else {
        break;
      }
    }

    if (allAlerts.length > 0) {
      const machines = yield* select(selectMachines);
      const machineIds = machines.map((m) => m.id);
      const grouped = groupByMachineId(allAlerts);
      for (const machineId of machineIds) {
        const machineAlerts = grouped[machineId] || [];
        const data = yield* call(() => createAlertsExpensive(machineAlerts));
        yield* put(
          rootActions.currentStatus.fetchAlertsForMachine.success({
            id: machineId,
            data,
          })
        );
      }
    }
  } catch (error) {
    console.error(error);
  }
}

function* fetchCarouselData() {
  // Don't load expensive carousel data if we aren't showing it
  const micro = yield* select(selectIsMicroView);
  if (micro) return;

  const lastFetched = yield* select(selectLastFetchedCarouselData);

  const client: CMSClientType = yield getContext("client");

  try {
    const allMachines = yield* select(selectMachines);
    const chunkedMachines = chunk(
      GUID_QUERY_CHUNK_AMOUNT_TIMESERIES_MACHINES,
      allMachines
    );
    let chunkNumber = 1;

    // Get all sensors with special "showOnCurrentStatus" display hint
    for (const machines of chunkedMachines) {
      const chunkId = `chunk ${chunkNumber} of ${chunkedMachines.length}`;
      yield* put(
        rootActions.currentStatus.fetchMachineCarouselData.request(chunkId)
      );
      let q = "$filter=displayHints__showOnCurrentStatus eq true";
      if (machines.length) {
        q += ` AND machineId in (${machines.map((m) => m.id).join(",")})`;
      }
      const sensors = yield* call(
        (query) => client.timeSeries.query(query).promise,
        q
      );
      if (!sensors.length) {
        yield* put(
          rootActions.currentStatus.fetchMachineCarouselData.success({
            id: chunkId,
            data: [],
          })
        );
        return;
      }

      const chunkedTimeSeries = chunk(
        GUID_QUERY_CHUNK_AMOUNT_TIMESERIES,
        sensors
      );
      const { from: start, to } = last24Hours();
      const from = lastFetched || start;

      // Get values for the sensors
      const sensorValues = yield* all(
        chunkedTimeSeries.map((timeSeries) => {
          const params = new URLSearchParams();
          params.append("from", from);
          params.append("to", to);
          params.append("sample", constants.sample.Day);
          for (const sensor of timeSeries) {
            params.append("timeSeriesId", sensor.id);
          }
          return call(
            (params) =>
              client.timeSeries.compactValues({
                query: params,
              }).promise,
            params
          );
        })
      );

      const sensorLimits = yield* all(
        chunkedTimeSeries.map((timeSeries) => {
          const params = new URLSearchParams();
          params.append("from", from);
          params.append("to", to);
          for (const sensor of timeSeries) {
            params.append("timeSeriesId", sensor.id);
          }
          return call(
            (params) =>
              client.timeSeries.limits({
                query: params,
              }).promise,
            params
          );
        })
      );

      const sensorValuesMap = sensorValues.reduce<
        MappyAbsolute<TimeSeriesValueCompactValue[]>
      >((obj, next) => ({ ...obj, ...next }), {});

      const data = Object.keys(sensorValuesMap)
        .filter((id) => {
          return sensors.find((s) => s.id === id);
        })
        .map((id) => {
          return {
            sensor: sensors.find((s) => s.id === id)!,
            values: sensorValuesMap[id],
            limits: sensorLimits[0]
              .map((limitRow) => limitRow)
              .filter((l) => l.timeSeriesId === id)!,
          };
        });

      yield* put(
        rootActions.currentStatus.fetchMachineCarouselData.success({
          id: chunkId,
          data,
        })
      );
      chunkNumber++;
    }
  } catch (ex) {
    console.warn(ex);
  }
}

function* fetchAllMachineStatusUpdates() {
  try {
    const machines = yield* select(selectMachines);
    const filtered = yield* select(selectAreMachinesFiltered);
    const machineList = filtered ? machines.map((m) => m.id) : [];
    const chunkedMachines = chunk(
      GUID_QUERY_CHUNK_AMOUNT_MACHINES,
      machineList
    );
    yield* all(
      chunkedMachines.map((machines) =>
        call(fetchMachineStatusUpdates, machines)
      )
    );
  } catch (e) {
    console.log(e);
  }
}

function* fetchMachineStatusUpdates(machines: string[]) {
  const client: CMSClientType = yield getContext("client");
  try {
    const lastFetched = yield* select((state) => state.global.lastFetched);
    const config = yield* call(() => getGlobalConfigs());
    const from = new Date(
      lastFetched || Date.now() - config.refreshRateCoreInMS
    ).toISOString();
    const statusUpdates = yield* call(
      (from) =>
        client.events.statusUpdates.all(machines, { query: { from } }).promise,
      from
    );
    yield* put(rootActions.global.setMachineStatusUpdates(statusUpdates));
  } catch (e) {
    console.log(e);
  }
}

function groupByMachineId(alerts: Alert[]) {
  return alerts.reduce(
    (acc, alert) => {
      const id = alert.machineId;
      if (!acc[id]) acc[id] = [];
      acc[id].push(alert); // no limit enforced
      return acc;
    },
    {} as Record<string, Alert[]>
  );
}
